import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Video } from "lucide-react";

const empty = { title: "", thumbnail: "", youtube_embed_url: "", description: "", featured: false };

export default function VideoManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch_ = () => api.get("/videos").then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetch_(); }, []);

  const openAdd = () => { setEditId(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (item) => { setEditId(item.id); setForm({ ...empty, ...item }); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editId) await api.put(`/videos/${editId}`, form);
      else await api.post("/videos", form);
      toast.success(editId ? "Video updated" : "Video added");
      setOpen(false);
      fetch_();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/videos/${id}`); toast.success("Deleted"); fetch_(); }
    catch { toast.error("Delete failed"); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-video-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">VIDEOS</h1>
          <p className="text-white/40 text-sm">{items.length} videos</p>
        </div>
        <Button data-testid="add-video-btn" onClick={openAdd} className="bg-[#D4AF37] text-black hover:bg-white text-sm"><Plus className="w-4 h-4 mr-1" /> Add Video</Button>
      </div>

      {loading ? <p className="text-white/30">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Video className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No videos yet. Add your first video.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-[#111111] border border-white/5 rounded overflow-hidden hover:border-white/10 transition-colors">
              <div className="aspect-video bg-[#0A0A0A] flex items-center justify-center relative overflow-hidden">
                {item.youtube_embed_url ? (
                  <iframe src={item.youtube_embed_url} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={item.title} />
                ) : item.thumbnail ? (
                  <img src={getFileUrl(item.thumbnail)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Video className="w-8 h-8 text-white/10" />
                )}
                {item.featured && <span className="absolute top-2 right-2 text-[10px] uppercase bg-[#D4AF37] text-black px-2 py-0.5 rounded font-bold z-10">Featured</span>}
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold truncate mb-1">{item.title}</p>
                <p className="text-xs text-white/30 truncate">{item.youtube_embed_url || "No URL"}</p>
                <div className="flex gap-2 mt-3">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-white/40 hover:text-white text-xs"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-white/40 hover:text-red-400 text-xs"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? "EDIT VIDEO" : "ADD VIDEO"}</DialogTitle>
            <DialogDescription className="text-white/40 text-xs">Fill in the video details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Title *</label>
              <input data-testid="video-title-input" value={form.title} onChange={e => setField("title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">YouTube Embed URL</label>
              <input value={form.youtube_embed_url} onChange={e => setField("youtube_embed_url", e.target.value)} placeholder="https://www.youtube.com/embed/..." className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Description</label>
              <textarea value={form.description} onChange={e => setField("description", e.target.value)} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] resize-none" />
            </div>
            <FileUpload label="Thumbnail" accept="image/*" value={form.thumbnail} onChange={v => setField("thumbnail", v)} />
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={v => setField("featured", v)} />
              <label className="text-sm text-white/60">Featured on homepage</label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/60">Cancel</Button>
              <Button data-testid="video-save-btn" onClick={handleSave} className="bg-[#D4AF37] text-black hover:bg-white">{editId ? "Update" : "Add"} Video</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
